import React, { useState } from 'react';
import WorkflowTabView from '../../../components/WorkflowTabView';
import { FileText, Building2, MapPin, Calendar, DollarSign } from 'lucide-react';
import ProjectBasics from './ProjectBasics';
import LegalEntities from './LegalEntities';
import Sites from './Sites';
import ReportingPeriod from './ReportingPeriod';
import CurrencyUnits from './CurrencyUnits';
import { useTranslation } from 'react-i18next';

const ScopeBoundariesWorkflow = ({ previewOnly = false }) => {
  const { t } = useTranslation();
  
  // Define steps configuration for the shared WorkflowTabView
  const steps = [
    {
      id: 1,
      title: t('workflow.scopeBoundaries.steps.projectBasics.title'),
      description: t('workflow.scopeBoundaries.steps.projectBasics.description'),
      icon: FileText,
      component: 'ProjectBasics'
    },
    {
      id: 2,
      title: t('workflow.scopeBoundaries.steps.legalEntities.title'),
      description: t('workflow.scopeBoundaries.steps.legalEntities.description'),
      icon: Building2,
      component: 'LegalEntities'
    },
    {
      id: 3,
      title: t('workflow.scopeBoundaries.steps.sites.title'),
      description: t('workflow.scopeBoundaries.steps.sites.description'),
      icon: MapPin,
      component: 'Sites'
    },
    {
      id: 4,
      title: t('workflow.scopeBoundaries.steps.reportingPeriod.title'),
      description: t('workflow.scopeBoundaries.steps.reportingPeriod.description'),
      icon: Calendar,
      component: 'ReportingPeriod'
    },
    {
      id: 5,
      title: t('workflow.scopeBoundaries.steps.currencyUnits.title'),
      description: t('workflow.scopeBoundaries.steps.currencyUnits.description'),
      icon: DollarSign,
      component: 'CurrencyUnits'
    }
  ];
  const loadFromStorage = () => {
    try {
      const savedData = localStorage.getItem('scopeBoundariesWorkflowData');
      const savedStep = localStorage.getItem('scopeBoundariesWorkflowStep');
      
      if (savedData) {
        return {
          formData: JSON.parse(savedData),
          currentStep: savedStep ? parseInt(savedStep) : 1
        };
      }
    } catch (error) {
      console.error('Error loading from localStorage:', error);
    }
    
    return {
      formData: {
        projectBasics: {},
        legalEntities: {},
        sites: {},
        reportingPeriod: {},
        currencyUnits: {}
      },
      currentStep: 1
    };
  };

  const { formData: initialFormData, currentStep: initialStep } = loadFromStorage();
  
  const [currentStep, setCurrentStep] = useState(initialStep);
  const [formData, setFormData] = useState(initialFormData);

  // Save data to localStorage
  const saveToStorage = (newFormData, newStep) => {
    try {
      localStorage.setItem('scopeBoundariesWorkflowData', JSON.stringify(newFormData));
      localStorage.setItem('scopeBoundariesWorkflowStep', newStep.toString());
    } catch (error) {
      console.error('Error saving to localStorage:', error);
    }
  };

  const handleStepChange = (stepId) => {
    setCurrentStep(stepId);
    saveToStorage(formData, stepId);
  };

  const handleSubmit = (stepData) => {
    const newFormData = {
      ...formData,
      [getStepKey(currentStep)]: stepData
    };
    setFormData(newFormData);
    saveToStorage(newFormData, currentStep);
    console.log('Step submitted:', currentStep, stepData);
  };

  const handleComplete = () => {
    // Finalize and redirect (placeholder)
    window.location.href = '/';
  };

  const getStepKey = (step) => {
    switch (step) {
      case 1: return 'projectBasics';
      case 2: return 'legalEntities';
      case 3: return 'sites';
      case 4: return 'reportingPeriod';
      case 5: return 'currencyUnits';
      default: return 'projectBasics';
    }
  };

  const renderCurrentStep = () => {
    const currentData = formData[getStepKey(currentStep)];

    switch (currentStep) {
      case 1:
        return (
          <ProjectBasics
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 2:
        return (
          <LegalEntities
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 3:
        return (
          <Sites
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 4:
        return (
          <ReportingPeriod
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      case 5:
        return (
          <CurrencyUnits
            onSubmit={handleSubmit}
            initialData={currentData}
          />
        );
      default:
        return <ProjectBasics />;
    }
  };

  return (
    <WorkflowTabView
      steps={steps}
      currentStep={currentStep}
      onStepChange={handleStepChange}
      onComplete={currentStep === 5 ? handleComplete : undefined}
      maxWidth="max-w-6xl"
      previewOnly={previewOnly}
    >
      {renderCurrentStep()}
    </WorkflowTabView>
  );
};

export default ScopeBoundariesWorkflow;
